/* LGB condensed size picker.
 * Turns a long "Size" button grid ("8ft x 6ft", "10ft x 6ft", ...) into two short rows:
 * pick a Width, then a Length. The theme's own Size buttons stay on the page (hidden) and
 * remain the single source of truth: choosing here simply clicks the matching real button,
 * so price, images, availability, sale badge, running total and add-on panels all keep
 * working exactly as before. No product data is changed.
 * If any size of a product can't be split cleanly into "width x length", that product
 * keeps the normal button grid.
 * The placeholder <div class="lgb-sz"> is rendered by snippets/variant-main-picker.liquid
 * directly before each button fieldset, with data-skip-subtree-update so the theme's
 * re-render (morph) leaves the built picker alone.
 *
 * 19 Oct 2026: gable-end labels + live size sketch.
 *  - On the gable-end ranges (vendor Sovereign or Shedlands, or a title starting "Vista Collection"
 *    or "Coppice Collection"; or any product whose size option is named "Size (length x gable end)")
 *    the two rows read "Length" and "Gable end", with a one-line hint. The option itself stays
 *    named "Size", so Search & Discovery's Size filter keeps working.
 *    A1 Garden Buildings (vendor A1, or a title starting "A1 "): A1 always quotes DEPTH x WIDTH,
 *    so the rows (and the floor plan) read "Depth" then "Width".
 *    Every other product keeps "Width" / "Length" exactly as before.
 *  - Under the chips a small inline SVG sketch redraws whenever the size changes (it is part of
 *    render(), which already runs on every variant change). Gable-end products get a schematic
 *    3D drawing (apex / pent / flat roof); everything else gets a top-down floor plan with area.
 *    Only drawn when both parts of the chosen size are clean measurements.
 */
(function () {
  if (window.__lgbSizePicker) return;
  window.__lgbSizePicker = true;

  var SEP_SPACED = /\s+[x×]\s+/i;
  var SEP_TIGHT = /^(.*?[0-9'"’”.a-z])\s*[x×]\s*([0-9].*)$/i;
  var SIZE_NAME = /size|dimension/i;
  var UNIT = /(\d+(?:\.\d+)?)\s*(feet|foot|ft|'|’|inches|inch|in|"|”|mm|cm|m)?/gi;

  function balanced(s) {
    return (s.match(/\(/g) || []).length === (s.match(/\)/g) || []).length;
  }
  function goodPart(s) {
    return s !== '' && /\d/.test(s) && balanced(s);
  }

  /* "10ft x 8ft" -> ["10ft", "8ft"]; null if it isn't exactly two parts */
  function split(v) {
    v = String(v).trim();
    var p = v.split(SEP_SPACED);
    if (p.length === 2) {
      var a = p[0].trim(), b = p[1].trim();
      if (goodPart(a) && goodPart(b)) return [a, b];
      return null;
    }
    if (p.length > 2) return null;
    var m = v.match(SEP_TIGHT);
    if (m) {
      var c = m[1].trim(), d = m[2].trim();
      if (/[x×]\s*\d/i.test(d)) return null;
      if (goodPart(c) && goodPart(d)) return [c, d];
    }
    return null;
  }

  /* "8ft 9in" -> 105, "6'3\"" -> 75, "2'6" -> 30, "12" -> 144 (bare numbers treated as feet).
     Returns null when the text isn't purely a measurement, e.g. "7ft inc verandah". */
  function inches(s) {
    s = String(s).replace(/\([^)]*\)/g, ' ').trim();
    if (!s) return null;
    var total = 0, found = false, lastFeet = false, pos = 0, m;
    UNIT.lastIndex = 0;
    while ((m = UNIT.exec(s)) !== null) {
      if (m[0] === '') { UNIT.lastIndex++; continue; }
      if (/[^\s,&+-]/.test(s.slice(pos, m.index))) return null;
      pos = m.index + m[0].length;
      var n = parseFloat(m[1]);
      var u = (m[2] || '').toLowerCase();
      if (u === 'ft' || u === 'feet' || u === 'foot' || u === "'" || u === '’') { total += n * 12; lastFeet = true; }
      else if (u === 'in' || u === 'inch' || u === 'inches' || u === '"' || u === '”') { total += n; lastFeet = false; }
      else if (u === 'mm') { total += n / 25.4; lastFeet = false; }
      else if (u === 'cm') { total += n / 2.54; lastFeet = false; }
      else if (u === 'm') { total += n * 39.37; lastFeet = false; }
      else { total += lastFeet ? n : n * 12; lastFeet = false; }
      found = true;
    }
    if (s.slice(pos).trim()) return null;
    return found ? total : null;
  }

  /* unique values in first-seen order, then numeric sort if every one can be measured */
  function orderAxis(list) {
    var seen = {}, out = [];
    list.forEach(function (x) { if (!seen.hasOwnProperty(x)) { seen[x] = 1; out.push(x); } });
    var keys = out.map(inches);
    if (keys.every(function (k) { return k !== null; })) {
      var idx = out.map(function (x, i) { return i; });
      idx.sort(function (a, b) { return (keys[a] - keys[b]) || (a - b); });
      out = idx.map(function (i) { return out[i]; });
    }
    return out;
  }

  /* Decide whether an option's values can be condensed. Returns a model or null. */
  function analyse(name, values) {
    if (values.length < 3) return null;
    var parts = [];
    for (var i = 0; i < values.length; i++) {
      var p = split(values[i]);
      if (!p) return null;
      parts.push(p);
    }
    if (!SIZE_NAME.test(name)) {
      /* not called "Size": only when every part is a clean measurement */
      for (var j = 0; j < parts.length; j++) {
        if (inches(parts[j][0]) === null || inches(parts[j][1]) === null) return null;
      }
    }
    var widths = orderAxis(parts.map(function (p) { return p[0]; }));
    var lengths = orderAxis(parts.map(function (p) { return p[1]; }));
    /* only worth it when it actually shortens the list */
    if (widths.length < 2 || widths.length >= values.length) return null;
    var combo = {};
    values.forEach(function (v, k) { combo[parts[k][0] + '\u0001' + parts[k][1]] = v; });
    return { widths: widths, lengths: lengths, combo: combo, parts: parts };
  }

  window.LGBSizeParse = { split: split, inches: inches, analyse: analyse, orderAxis: orderAxis };

  var CSS =
    '.lgb-sz:empty{display:none}' +
    '.lgb-sz:has(>.lgb-sz__ui)+fieldset.variant-option{display:none!important}' +
    '.variant-option+.lgb-sz+.variant-option{margin-top:var(--padding-lg)}' +
    '.variant-option+.lgb-sz:has(>.lgb-sz__ui){margin-top:var(--padding-lg)}' +
    '.lgb-sz__ui{display:flex;flex-direction:column;gap:var(--padding-lg,14px)}' +
    '.lgb-sz__group{display:flex;flex-direction:column;margin:0}' +
    '.lgb-sz__legend{display:block;margin-block-end:var(--margin-xs,8px);color:var(--variant-option-label-color,var(--color-foreground))}' +
    '.lgb-sz__legend b{font-weight:inherit;padding-inline-start:var(--padding-xs,6px);color:var(--variant-option-label-color-subdued,var(--color-foreground-subdued,inherit))}' +
    '.lgb-sz__row{display:flex;flex-wrap:wrap;gap:var(--gap-sm,8px)}' +
    '.lgb-sz__btn{font:inherit;letter-spacing:inherit;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;' +
      'min-height:3.25em;min-width:3.25em;padding:var(--padding-sm,8px) var(--padding-lg,14px);white-space:nowrap;' +
      'border:var(--options-border-width,1px) solid var(--color-variant-border);border-radius:var(--options-border-radius,6px);' +
      'background-color:var(--color-variant-background);color:var(--color-variant-text);' +
      'transition:background-color .15s ease,border-color .15s ease,color .15s ease}' +
    '.lgb-sz__btn:hover{background-color:var(--color-variant-hover-background);border-color:var(--color-variant-hover-border);color:var(--color-variant-hover-text)}' +
    '.lgb-sz__btn[aria-pressed="true"]{background-color:var(--color-selected-variant-background);border-color:var(--color-selected-variant-border);color:var(--color-selected-variant-text)}' +
    '.lgb-sz__btn[aria-pressed="true"]:hover{background-color:var(--color-selected-variant-hover-background);border-color:var(--color-selected-variant-hover-border);color:var(--color-selected-variant-hover-text)}' +
    '.lgb-sz__btn.is-unavailable{color:rgb(var(--color-variant-text-rgb) / var(--opacity-60,.6));text-decoration:line-through;border-style:dashed}' +
    '.lgb-sz__btn.is-unavailable[aria-pressed="true"]{color:rgb(var(--color-selected-variant-text-rgb) / var(--opacity-subdued-text,.7))}' +
    '.lgb-sz__btn:focus-visible{outline:var(--focus-outline-width,2px) solid var(--color-foreground);outline-offset:var(--focus-outline-offset,2px)}' +
    '@media screen and (min-width:750px){.lgb-sz__btn{padding:var(--padding-xs,6px) var(--padding-md,12px)}}' +
    '@media (prefers-reduced-motion:reduce){.lgb-sz__btn{transition:none}}' +
    '.lgb-sz__hint{margin:0;font-size:.85em;line-height:1.4;color:var(--variant-option-label-color-subdued,var(--color-foreground-subdued,inherit))}' +
    '.lgb-sz__sketch{margin:0;max-width:100%;min-width:0}' +
    '.lgb-sz__sketch svg{display:block;width:100%;max-width:100%;height:auto;max-height:220px}' +
    '.lgb-sz__cap{margin:4px 0 0;font-size:.78em;line-height:1.35;opacity:.75}';

  function addCss() {
    if (document.getElementById('lgb-sz-css')) return;
    var st = document.createElement('style');
    st.id = 'lgb-sz-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  var uid = 0;
  var mounted = typeof WeakMap === 'function' ? new WeakMap() : null;

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- size sketch (inline SVG, no libraries) ---------- */
  var GABLE_NAME = /^size\s*\(\s*length\s*x\s*gable\s*end\s*\)/i;
  var INK = '#34493b', WALL = '#eef3ee', WALL2 = '#dfe8df', ROOF = '#c5d3c5', TXT = '#34493b';

  function fmt(n) { return (Math.round(n * 10) / 10).toString(); }
  function pts(a) { return a.map(function (p) { return fmt(p[0]) + ',' + fmt(p[1]); }).join(' '); }
  function poly(a, fill) {
    return '<polygon points="' + pts(a) + '" fill="' + fill + '" stroke="' + INK + '" stroke-width="1.5" stroke-linejoin="round"/>';
  }
  function txtW(s, fs) { return String(s).length * fs * 0.56; }

  /* roof shape for gable-end buildings, from the roof_style metafield and the title */
  function roofOf(info) {
    var t = (info.roof || '') + ' ' + (info.title || '');
    if (/apex|apent|barn|gambrel/i.test(t)) return 'apex';
    if (/pent/i.test(t)) return 'pent';
    if (/hip|flat|octag|bandstand/i.test(t)) return 'flat';
    if (/garden shed|summerhouse|potting shed|^shed$|playhouse|garage/i.test(info.type || '')) return 'apex';
    return 'flat';
  }

  /* lay out shapes + labels in model units, then fit everything into a viewBox */
  function svgWrap(shapes, labels, minX, minY, maxX, maxY, title) {
    labels.forEach(function (l) {
      var w = txtW(l.t, l.fs);
      var x0 = l.a === 'end' ? l.x - w : (l.a === 'middle' ? l.x - w / 2 : l.x);
      minX = Math.min(minX, x0); maxX = Math.max(maxX, x0 + w);
      minY = Math.min(minY, l.y - l.fs); maxY = Math.max(maxY, l.y + 4);
    });
    var pad = 6, vw = maxX - minX + pad * 2, vh = maxY - minY + pad * 2;
    var out = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + fmt(minX - pad) + ' ' + fmt(minY - pad) + ' ' + fmt(vw) + ' ' + fmt(vh) +
      '" role="img" aria-label="' + esc(title) + '" preserveAspectRatio="xMidYMid meet" focusable="false">' + shapes;
    labels.forEach(function (l) {
      out += '<text x="' + fmt(l.x) + '" y="' + fmt(l.y) + '" font-size="' + l.fs + '" font-family="inherit" font-weight="600" fill="' + TXT +
        '" text-anchor="' + l.a + '">' + esc(l.t) + '</text>';
    });
    return out + '</svg>';
  }

  /* 3D-ish (isometric) sketch: length along one axis, gable end along the other */
  function gableSketch(lenIn, gabIn, lenTxt, gabTxt, roof) {
    var L = lenIn / 12, G = gabIn / 12;
    var big = Math.max(L, G), S = 220 / Math.max(big * 0.87 * 2, 1);
    S = Math.min(S, 260 / ((L + G) * 0.87));
    var c = 0.866, s = 0.5;
    var Lv = [L * c * S, -L * s * S], Gv = [-G * c * S, -G * s * S];
    var hW = 6.5 * S * 0.9;                           /* fixed, schematic wall height */
    var rise = roof === 'apex' ? Math.max(G * 0.32, 1.6) * S : (roof === 'pent' ? 1.4 * S : 0);
    function add() { var x = 0, y = 0; for (var i = 0; i < arguments.length; i++) { x += arguments[i][0]; y += arguments[i][1]; } return [x, y]; }
    var O = [0, 0], H = [0, -hW], up = [0, -rise];
    var A = O, B = add(O, Lv), C = add(O, Gv), D = add(O, Lv, Gv);
    var shapes = '', all = [A, B, C, D, add(A, H), add(B, H), add(C, H), add(D, H)];

    if (roof === 'apex') {
      var R0 = add(A, H, [Gv[0] / 2, Gv[1] / 2], up), R1 = add(R0, Lv);
      all.push(R0, R1);
      shapes += poly([add(C, H), R0, R1, add(D, H)], ROOF);               /* back roof slope */
      shapes += poly([A, C, add(C, H), add(A, H)], WALL2);                /* gable end wall */
      shapes += poly([add(A, H), add(C, H), R0], WALL2);                  /* gable triangle */
      shapes += poly([A, B, add(B, H), add(A, H)], WALL);                 /* long side wall */
      shapes += poly([add(A, H), add(B, H), R1, R0], ROOF);               /* front roof slope */
    } else if (roof === 'pent') {
      /* front (long, nearest) wall is the tall side; the roof falls away to the back wall */
      var A2 = add(A, H, up), B2 = add(B, H, up);
      all.push(A2, B2);
      shapes += poly([A, C, add(C, H), A2], WALL2);                       /* gable end: slopes down front to back */
      shapes += poly([A, B, B2, A2], WALL);                               /* tall front wall */
      shapes += poly([A2, B2, add(D, H), add(C, H)], ROOF);
    } else {
      shapes += poly([A, C, add(C, H), add(A, H)], WALL2);
      shapes += poly([A, B, add(B, H), add(A, H)], WALL);
      shapes += poly([add(A, H), add(B, H), add(D, H), add(C, H)], ROOF);
    }
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    all.forEach(function (p) { minX = Math.min(minX, p[0]); maxX = Math.max(maxX, p[0]); minY = Math.min(minY, p[1]); maxY = Math.max(maxY, p[1]); });
    var fs = 12;
    var mL = [(A[0] + B[0]) / 2 + 6, (A[1] + B[1]) / 2 + 14];
    var mG = [(A[0] + C[0]) / 2 - 6, (A[1] + C[1]) / 2 + 14];
    var labels = [
      { t: 'Length ' + lenTxt, x: mL[0], y: mL[1], a: 'start', fs: fs },
      { t: 'Gable end ' + gabTxt, x: mG[0], y: mG[1], a: 'end', fs: fs }
    ];
    return svgWrap(shapes, labels, minX, minY, maxX, maxY,
      'Sketch: length ' + lenTxt + ', gable end ' + gabTxt + (roof === 'flat' ? '' : ', ' + roof + ' roof'));
  }

  /* top-down floor plan for every other product */
  function planSketch(aIn, bIn, aLbl, aTxt, bLbl, bTxt) {
    var A = aIn / 12, B = bIn / 12;
    var S = Math.min(240 / A, 130 / B);
    var w = A * S, h = B * S, fs = 12;
    var area = Math.round(A * B * 10) / 10;
    var shapes = '<rect x="0" y="0" width="' + fmt(w) + '" height="' + fmt(h) + '" fill="' + WALL + '" stroke="' + INK + '" stroke-width="1.5"/>' +
      '<text x="' + fmt(w / 2) + '" y="' + fmt(h / 2 + 4) + '" font-size="11" font-family="inherit" fill="' + TXT + '" text-anchor="middle">' + esc(fmt(area) + ' sq ft') + '</text>';
    var labels = [
      { t: aLbl + ' ' + aTxt, x: w / 2, y: h + 16, a: 'middle', fs: fs },
      { t: bLbl + ' ' + bTxt, x: w + 8, y: h / 2 + 4, a: 'start', fs: fs }
    ];
    return svgWrap(shapes, labels, 0, 0, w, h, 'Floor plan: ' + aLbl + ' ' + aTxt + ', ' + bLbl + ' ' + bTxt + ', ' + fmt(area) + ' sq ft');
  }

  /* returns the sketch HTML for a chosen pair, or '' when it can't be drawn */
  function sketch(gable, W, L, info, labW, labL) {
    if (!W || !L) return '';
    var a = inches(W), b = inches(L);
    if (!a || !b || a < 12 || b < 12) return '';
    var svg;
    try {
      svg = gable ? gableSketch(a, b, W, L, roofOf(info || {})) : planSketch(a, b, labW, W, labL, L);
    } catch (e) { return ''; }
    return '<figure class="lgb-sz__sketch">' + svg +
      '<figcaption class="lgb-sz__cap">Sketch for guidance only &ndash; not to scale for height.</figcaption></figure>';
  }

  /* Gable-end ranges, worked out from the product itself (not the option name).
     Vendor comes from data-lgb-sz-vendor when the snippet provides it; otherwise the title,
     which on this store always starts with the vendor for these ranges ("Sovereign – ...",
     "Shedlands – ...", "Vista Collection – ...", "Coppice Collection – ..."). */
  var GABLE_VENDOR = /^\s*(sovereign|shedlands)\s*$/i;
  var GABLE_TITLE = /^\s*(sovereign|shedlands)\b|^\s*(vista|coppice)\s+collection\b/i;
  function isGableProduct(holder) {
    var vp = holder.closest ? holder.closest('variant-picker') : null;
    var vendor = holder.getAttribute('data-lgb-sz-vendor') || (vp ? vp.getAttribute('data-product-vendor') : '') || '';
    if (vendor) return GABLE_VENDOR.test(vendor);
    var title = holder.getAttribute('data-lgb-sz-title') || (vp ? vp.getAttribute('data-product-title') : '') || '';
    return GABLE_TITLE.test(title);
  }

  var A1_VENDOR = /^\s*a1 garden buildings\s*$/i, A1_TITLE = /^\s*a1\s/i;
  function isA1Product(holder) {
    var vp = holder.closest ? holder.closest('variant-picker') : null;
    var vendor = holder.getAttribute('data-lgb-sz-vendor') || (vp ? vp.getAttribute('data-product-vendor') : '') || '';
    if (vendor) return A1_VENDOR.test(vendor);
    return A1_TITLE.test(holder.getAttribute('data-lgb-sz-title') || (vp ? vp.getAttribute('data-product-title') : '') || '');
  }

  window.LGBSizeSketch = { sketch: sketch, roofOf: roofOf, gableName: GABLE_NAME, gableTitle: GABLE_TITLE };

  /* Roof style and product type for the sketch come from the store's own compare data
     (/products/<handle>?view=lgbcx, templates/product.lgbcx.liquid): fetched once per product,
     only for gable-end products. Until it arrives the title alone is used. */
  var INFO = {};
  function productInfo(holder, onReady) {
    var vp = holder.closest ? holder.closest('variant-picker') : null;
    var handle = vp ? vp.getAttribute('data-product-handle') : '';
    var info = {
      roof: holder.getAttribute('data-lgb-sz-roof') || '',
      title: holder.getAttribute('data-lgb-sz-title') || (vp ? vp.getAttribute('data-product-title') : '') || '',
      type: holder.getAttribute('data-lgb-sz-type') || ''
    };
    if (!handle || typeof fetch !== 'function') return info;
    var got = INFO[handle];
    if (got && got !== 'pending') {
      if (!info.roof) info.roof = got.roof || '';
      if (!info.type) info.type = got.type || '';
      return info;
    }
    if (!got) {
      INFO[handle] = 'pending';
      fetch('/products/' + encodeURIComponent(handle) + '?view=lgbcx', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) { INFO[handle] = d ? { roof: d.roof || '', type: d.ty || '' } : {}; onReady(); })
        .catch(function () { INFO[handle] = {}; });
    }
    return info;
  }

  function Picker(holder) {
    this.holder = holder;
    this.id = 'lgbsz' + (++uid);
    this.sig = '';
    this.model = null;
    this.modelKey = '';
  }

  Picker.prototype.fieldset = function () {
    var fs = this.holder.nextElementSibling;
    return fs && fs.tagName === 'FIELDSET' && fs.classList.contains('variant-option--buttons') &&
      !fs.classList.contains('variant-option--swatches') ? fs : null;
  };

  Picker.prototype.radios = function (fs) {
    return Array.prototype.slice.call(fs.querySelectorAll('input[type="radio"]'));
  };

  Picker.prototype.teardown = function () {
    if (this.holder.firstChild) this.holder.innerHTML = '';
    this.sig = '';
  };

  Picker.prototype.sync = function () {
    var fs = this.fieldset();
    if (!fs) { this.teardown(); return; }
    var radios = this.radios(fs);
    var values = radios.map(function (r) { return r.value; });
    var key = values.join('\u0002');
    if (key !== this.modelKey) {
      this.modelKey = key;
      var name = this.holder.getAttribute('data-lgb-sz-name') ||
        (radios[0] && radios[0].getAttribute('data-option-name')) || '';
      this.model = analyse(name, values);
      if (this.model) {
        this.model.gable = GABLE_NAME.test(name) || isGableProduct(this.holder);
        this.model.a1 = !this.model.gable && isA1Product(this.holder);
      }
      this.sig = '';
    }
    if (!this.model) { this.teardown(); return; }

    var byValue = {}, checked = null;
    radios.forEach(function (r) {
      byValue[r.value] = r;
      if (r.checked) checked = r.value;
    });
    var sig = key + '|' + (checked || '') + '|' + radios.map(function (r) {
      return r.getAttribute('data-option-available');
    }).join(',');
    if (sig === this.sig && this.holder.firstChild) return;
    this.sig = sig;
    this.render(byValue, checked);
  };

  Picker.prototype.render = function (byValue, checked) {
    var m = this.model, self = this;
    var cur = checked ? split(checked) : null;
    var W = cur ? cur[0] : null, L = cur ? cur[1] : null;

    function avail(v) {
      var r = byValue[v];
      return !!r && r.getAttribute('data-option-available') !== 'false';
    }
    function widthAvail(w) {
      return m.lengths.some(function (l) {
        var v = m.combo[w + '\u0001' + l];
        return v && avail(v);
      });
    }

    var focusKey = null;
    var ae = document.activeElement;
    if (ae && this.holder.contains(ae) && ae.getAttribute) {
      focusKey = ae.getAttribute('data-axis') + '\u0003' + ae.getAttribute('data-val');
    }

    var wBtns = m.widths.map(function (w) {
      var on = w === W, ok = widthAvail(w);
      return '<button type="button" class="lgb-sz__btn' + (ok ? '' : ' is-unavailable') + '" data-axis="w" data-val="' + esc(w) +
        '" aria-pressed="' + on + '"' + (ok ? '' : ' aria-label="' + esc(w) + ' - sold out"') + '>' + esc(w) + '</button>';
    }).join('');

    var lens = W ? m.lengths.filter(function (l) { return m.combo[W + '\u0001' + l]; }) : [];
    var lBtns = lens.map(function (l) {
      var v = m.combo[W + '\u0001' + l], on = l === L, ok = avail(v);
      return '<button type="button" class="lgb-sz__btn' + (ok ? '' : ' is-unavailable') + '" data-axis="l" data-val="' + esc(l) +
        '" aria-pressed="' + on + '"' + (ok ? '' : ' aria-label="' + esc(l) + ' - sold out"') + '>' + esc(l) + '</button>';
    }).join('');

    var id = this.id;
    var labW = m.gable ? 'Length' : (m.a1 ? 'Depth' : 'Width'), labL = m.gable ? 'Gable end' : (m.a1 ? 'Width' : 'Length');
    var info = m.gable ? productInfo(this.holder, function () { self.sig = ''; self.sync(); }) : {};
    var extra = '';
    try { extra = sketch(m.gable, W, L, info, labW, labL); } catch (e) { extra = ''; }
    this.holder.innerHTML =
      '<div class="lgb-sz__ui">' +
        '<div class="variant-option lgb-sz__group" role="group" aria-labelledby="' + id + '-w">' +
          '<span class="lgb-sz__legend" id="' + id + '-w">' + labW + '<b>' + (W ? esc(W) : '') + '</b></span>' +
          '<div class="lgb-sz__row">' + wBtns + '</div>' +
        '</div>' +
        '<div class="variant-option lgb-sz__group" role="group" aria-labelledby="' + id + '-l">' +
          '<span class="lgb-sz__legend" id="' + id + '-l">' + labL + '<b>' + (L ? esc(L) : '') + '</b></span>' +
          '<div class="lgb-sz__row">' + lBtns + '</div>' +
        '</div>' +
        (m.gable ? '<p class="lgb-sz__hint">Gable end = the end of the building where the roof comes to a point (on pent roofs, the sloping side).</p>' : '') +
        extra +
      '</div>';

    if (focusKey) {
      var btns = this.holder.querySelectorAll('.lgb-sz__btn');
      for (var i = 0; i < btns.length; i++) {
        if (btns[i].getAttribute('data-axis') + '\u0003' + btns[i].getAttribute('data-val') === focusKey) { btns[i].focus(); break; }
      }
    }

    if (!this.bound) {
      this.bound = true;
      this.holder.addEventListener('click', function (e) {
        var b = e.target && e.target.closest ? e.target.closest('.lgb-sz__btn') : null;
        if (!b || !self.holder.contains(b)) return;
        e.preventDefault();
        self.choose(b.getAttribute('data-axis'), b.getAttribute('data-val'));
      });
    }
  };

  Picker.prototype.choose = function (axis, val) {
    var fs = this.fieldset();
    if (!fs || !this.model) return;
    var m = this.model, radios = this.radios(fs), byValue = {}, checked = null;
    radios.forEach(function (r) { byValue[r.value] = r; if (r.checked) checked = r.value; });
    var cur = checked ? split(checked) : null;
    var W = cur ? cur[0] : m.widths[0], L = cur ? cur[1] : null;
    var target = null;

    if (axis === 'w') {
      W = val;
      var lens = m.lengths.filter(function (l) { return m.combo[W + '\u0001' + l]; });
      if (L && m.combo[W + '\u0001' + L]) target = m.combo[W + '\u0001' + L];
      else {
        var firstOk = lens.filter(function (l) {
          var r = byValue[m.combo[W + '\u0001' + l]];
          return r && r.getAttribute('data-option-available') !== 'false';
        })[0];
        var pick = firstOk || lens[0];
        if (pick) target = m.combo[W + '\u0001' + pick];
      }
    } else {
      target = m.combo[W + '\u0001' + val] || null;
    }
    if (!target || target === checked) return;
    var radio = byValue[target];
    if (!radio) return;
    /* the theme's own button: fires the same change event a shopper's click would */
    radio.click();
    this.sync();
  };

  var pickers = [];

  function scan() {
    var holders = document.querySelectorAll('.lgb-sz');
    for (var i = 0; i < holders.length; i++) {
      var h = holders[i], p = mounted ? mounted.get(h) : h.__lgbSz;
      if (!p) {
        p = new Picker(h);
        if (mounted) mounted.set(h, p); else h.__lgbSz = p;
        pickers.push(p);
      }
    }
    pickers = pickers.filter(function (p) { return document.documentElement.contains(p.holder); });
    pickers.forEach(function (p) {
      try { p.sync(); } catch (err) { p.teardown(); if (window.console) console.warn('LGB size picker:', err); }
    });
  }

  var queued = false;
  function queue() {
    if (queued) return;
    queued = true;
    (window.requestAnimationFrame || setTimeout)(function () { queued = false; scan(); });
  }

  function start() {
    addCss();
    scan();
    document.addEventListener('change', queue, true);
    if (window.MutationObserver) {
      new MutationObserver(function (list) {
        for (var i = 0; i < list.length; i++) {
          var t = list[i].target;
          if (t && t.closest && t.closest('.lgb-sz__ui')) continue;
          queue();
          return;
        }
      }).observe(document.body, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['checked', 'data-option-available', 'data-current-checked', 'value']
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
